import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageCircle, Send, RefreshCw, RotateCcw, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const TOKEN_OPTS = [
  { value: "{nome}", label: "Nome del lead" },
  { value: "{cognome}", label: "Cognome del lead" },
  { value: "{nome_completo}", label: "Nome e cognome" },
  { value: "__fixed__", label: "Testo fisso…" },
];
const SAMPLE = { "{nome}": "Mario", "{cognome}": "Rossi", "{nome_completo}": "Mario Rossi" };
const keyOf = (t) => `${t.account_id || ""}|${t.name}|${t.language}`;

const WaStatus = ({ st, onRetry, onCancel }) => (
  <div className="space-y-2" data-testid="wa-status">
    {st.scheduled > 0 && (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-sky-200 dark:border-sky-900 bg-sky-50 dark:bg-sky-950/30 p-2 text-sm" data-testid="wa-scheduled-banner">
        <span><CalendarClock className="inline h-4 w-4 mr-1.5 text-sky-600" />{st.scheduled} messaggi programmati per <b>{new Date(st.scheduled_at).toLocaleString("it-IT", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })}</b></span>
        <Button size="sm" variant="outline" onClick={onCancel} data-testid="wa-cancel-schedule-btn">Annulla programmazione</Button>
      </div>
    )}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
      {[["Inviati", st.sent, "text-emerald-600"], ["In coda", st.queued + st.sending, "text-sky-600"], ["Falliti", st.failed, "text-rose-600"],
        ["Ultimo invio", st.last_sent_at ? new Date(st.last_sent_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—", "text-foreground"]].map(([l, v, c]) => (
        <div key={l} className="rounded-md border border-border p-2"><div className={`text-lg font-semibold ${c}`} data-testid={`wa-status-${l.toLowerCase().replace(" ", "-")}`}>{v}</div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">{l}</div></div>
      ))}
    </div>
    {st.failed > 0 && (
      <div className="rounded-md border border-rose-200 dark:border-rose-900 p-2 text-xs space-y-1">
        <div className="flex items-center justify-between"><span className="font-medium text-rose-600">Ultimi invii falliti</span>
          <Button size="sm" variant="outline" onClick={onRetry} data-testid="wa-retry-btn"><RotateCcw className="h-3.5 w-3.5 mr-1" />Riprova falliti</Button></div>
        {st.failed_items.map((f, i) => <div key={i} className="text-muted-foreground break-all">{f.name || "—"} · {f.phone} · {f.error || "Zernio non ha risposto in tempo (timeout)"}</div>)}
      </div>
    )}
  </div>
);

export const WhatsAppCampaignCard = ({ campaign }) => {
  const cid = campaign.id;
  const [templates, setTemplates] = useState(null);
  const [tplErr, setTplErr] = useState("");
  const [sel, setSel] = useState(campaign.wa_template_name ? `${campaign.wa_account_id || ""}|${campaign.wa_template_name}|${campaign.wa_template_language}` : "");
  const [params, setParams] = useState(campaign.wa_params || []);
  const [testPhone, setTestPhone] = useState("");
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("now");
  const [when, setWhen] = useState("");
  const tpl = (templates || []).find((t) => keyOf(t) === sel);

  const loadStatus = useCallback(() => { api.get(`/campaigns/${cid}/whatsapp/status`).then((r) => setSt(r.data)).catch(() => {}); }, [cid]);
  useEffect(() => {
    api.get("/whatsapp/templates").then((r) => setTemplates(r.data)).catch((e) => { setTemplates([]); setTplErr(formatApiErrorDetail(e.response?.data?.detail) || "Errore lettura modelli"); });
    loadStatus();
  }, [loadStatus]);
  useEffect(() => {
    if (!st || st.queued + st.sending - (st.scheduled || 0) <= 0) return undefined;
    const t = setInterval(loadStatus, 5000);
    return () => clearInterval(t);
  }, [st, loadStatus]);

  const pickTemplate = (k) => {
    setSel(k);
    const t = templates.find((x) => keyOf(x) === k);
    setParams(Array.from({ length: t?.vars || 0 }, (_, i) => params[i] ?? (i === 0 ? "{nome}" : "")));
  };
  const setParam = (i, v) => setParams(params.map((p, j) => (j === i ? v : p)));
  const isToken = (p) => SAMPLE[p] !== undefined;
  const preview = (tpl?.body || "").replace(/\{\{(\d+)\}\}/g, (_, n) => { const p = params[Number(n) - 1]; return p ? (SAMPLE[p] ?? p) : `{{${n}}}`; });

  const save = async () => {
    if (!tpl) { toast.error("Seleziona un modello"); return false; }
    if (params.some((p) => !p || !p.trim())) { toast.error("Compila tutte le variabili del messaggio"); return false; }
    try { await api.put(`/campaigns/${cid}/whatsapp`, { template_name: tpl.name, template_language: tpl.language, account_id: tpl.account_id, params }); toast.success("Messaggio salvato"); return true; }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); return false; }
  };
  const test = async () => {
    if (!testPhone) { toast.error("Inserisci il tuo cellulare"); return; }
    if (!(await save())) return;
    setBusy(true);
    try { const { data } = await api.post(`/campaigns/${cid}/whatsapp/test`, { phone: testPhone }); toast.success(`Messaggio di prova inviato a +${data.to}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore invio prova"); } finally { setBusy(false); }
  };
  const send = async () => {
    let scheduled_at = null;
    if (mode === "later") {
      if (!when) { toast.error("Scegli giorno e ora di partenza"); return; }
      const d = new Date(when);
      if (d <= new Date()) { toast.error("La partenza deve essere nel futuro"); return; }
      scheduled_at = d.toISOString();
    }
    const quando = scheduled_at ? `il ${new Date(scheduled_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}` : "subito";
    if (!window.confirm(`Inviare ${quando} il messaggio WhatsApp a tutti i ${campaign.lead_count} lead della campagna con cellulare valido? I lead già raggiunti non riceveranno un secondo messaggio.`)) return;
    if (!(await save())) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/campaigns/${cid}/whatsapp/send`, { scheduled_at });
      toast.success(data.scheduled_at ? `${data.queued} messaggi programmati per ${new Date(data.scheduled_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}` : `${data.queued} messaggi in coda · ${data.skipped} saltati (senza cellulare o già inviati)`);
      loadStatus();
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); } finally { setBusy(false); }
  };
  const cancelSchedule = async () => {
    if (!window.confirm("Annullare l'invio programmato?")) return;
    const { data } = await api.post(`/campaigns/${cid}/whatsapp/cancel-schedule`); toast.success(`${data.cancelled} messaggi programmati annullati`); loadStatus();
  };
  const retry = async () => { const { data } = await api.post(`/campaigns/${cid}/whatsapp/retry`); toast.success(`${data.requeued} messaggi rimessi in coda`); loadStatus(); };

  return (
    <Card className="p-5 space-y-4" data-testid="campaign-whatsapp-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading font-semibold flex items-center gap-2"><MessageCircle className="h-4 w-4 text-emerald-600" /> Messaggio WhatsApp</h2>
        <Button variant="ghost" size="sm" onClick={loadStatus} data-testid="wa-status-refresh"><RefreshCw className="h-4 w-4 mr-1.5" />Aggiorna stato</Button>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Modello approvato (da Zernio)</Label>
            <Select value={sel || undefined} onValueChange={pickTemplate} disabled={!templates?.length}>
              <SelectTrigger data-testid="wa-template-select"><SelectValue placeholder={templates === null ? "Caricamento…" : templates.length ? "Seleziona un modello" : "Nessun modello disponibile"} /></SelectTrigger>
              <SelectContent>{(templates || []).map((t) => (
                <SelectItem key={keyOf(t)} value={keyOf(t)} disabled={!t.supported}>{t.name} · {t.category} · {t.account_name}{!t.supported ? " (non supportato)" : ""}</SelectItem>
              ))}</SelectContent>
            </Select>
            {tplErr && <p className="text-xs text-rose-600" data-testid="wa-template-error">{tplErr}</p>}
            {templates?.length > 0 && campaign.wa_template_name && sel && !tpl && <p className="text-xs text-amber-600" data-testid="wa-saved-missing">Il modello salvato «{campaign.wa_template_name}» non è più tra quelli approvati: scegline un altro.</p>}
            {tpl && <p className="text-xs text-muted-foreground" data-testid="wa-sender-number">Inviato dal numero WhatsApp <b>{tpl.account_name}</b></p>}
            {tpl && tpl.category !== "MARKETING" && <p className="text-xs text-amber-600" data-testid="wa-category-warning">Attenzione: per le campagne promozionali Meta richiede modelli di categoria MARKETING.</p>}
          </div>
          {params.map((p, i) => (
            <div key={i} className="grid grid-cols-[70px_1fr] items-center gap-2" data-testid={`wa-param-${i + 1}`}>
              <span className="text-xs font-mono text-muted-foreground">{`{{${i + 1}}}`}</span>
              <div className="flex gap-2">
                <Select value={isToken(p) ? p : "__fixed__"} onValueChange={(v) => setParam(i, v === "__fixed__" ? "" : v)}>
                  <SelectTrigger className="w-44" data-testid={`wa-param-${i + 1}-type`}><SelectValue /></SelectTrigger>
                  <SelectContent>{TOKEN_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
                {!isToken(p) && <Input value={p} onChange={(e) => setParam(i, e.target.value)} placeholder="Testo fisso" data-testid={`wa-param-${i + 1}-text`} />}
              </div>
            </div>
          ))}
          <div className="space-y-2 pt-1">
            <Label>Partenza</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="w-44" data-testid="wa-send-mode"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="now">Invia subito</SelectItem><SelectItem value="later">Programma invio</SelectItem></SelectContent>
              </Select>
              {mode === "later" && <Input type="datetime-local" className="w-56" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="wa-send-when" />}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button variant="outline" onClick={save} data-testid="wa-save-btn">Salva messaggio</Button>
            <Button onClick={send} disabled={busy || !tpl} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wa-send-btn"><Send className="h-4 w-4 mr-2" />{mode === "later" ? "Programma campagna" : "Invia campagna"}</Button>
          </div>
          <div className="flex gap-2 items-end">
            <div className="space-y-1.5 flex-1"><Label>Invia prova al tuo cellulare</Label><Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="es. 333 1234567" data-testid="wa-test-phone" /></div>
            <Button variant="outline" onClick={test} disabled={busy || !tpl} data-testid="wa-test-btn">Invia prova</Button>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Anteprima (esempio con Mario Rossi)</Label>
          <div className="rounded-xl bg-[#e7ddd3] dark:bg-emerald-950/40 p-4 min-h-[140px]">
            <div className="max-w-[90%] rounded-lg rounded-tl-none bg-white dark:bg-slate-800 px-3 py-2 text-sm shadow-sm whitespace-pre-wrap" data-testid="wa-preview">{preview || <span className="text-muted-foreground">Seleziona un modello per vedere l'anteprima</span>}</div>
          </div>
        </div>
      </div>
      {st && <WaStatus st={st} onRetry={retry} onCancel={cancelSchedule} />}
    </Card>
  );
};
